import api from '@/api/api.js'
import { DataLoadState } from '@/utils/storeUtils.js'

export const ADS_STORE_NAMESPACE = 'ads'

// Mutation names
const SET_ADS_LOAD_STARTED = 'setAdsLoadStarted'
const SET_ADS_LOAD_FINISHED_SUCCESSFULLY = 'setAdsLoadFinishedSuccessfully'
const SET_ADS_LOAD_FAILED = 'setAdsLoadFailed'
const SET_SELECTED_AD_ID = 'setSelectedAdId'

const SET_ADS_META_DATA_LOAD_STARTED = 'setAdsMetaDataLoadStarted'
const SET_ADS_META_DATA_LOAD_FINISHED_SUCCESSFULLY = 'setAdsMetaDataLoadFinishedSuccessfully'
const SET_ADS_META_DATA_LOAD_FAILED = 'setAdsMetaDataLoadFailed'

const SET_SELECTED_AD_PERIODS_LOAD_STARTED = 'setSelectedAdPeriodsLoadStarted'
const SET_SELECTED_AD_PERIODS_LOAD_FINISHED_SUCCESSFULLY =
  'setSelectedAdPeriodsLoadFinishedSuccessfully'
const SET_SELECTED_AD_PERIODS_LOAD_FAILED = 'setSelectedAdPeriodsLoadFailed'
const RESET_SELECTED_AD_PERIODS = 'resetSelectedAdPeriods'

// Getter names
export const IS_LOADING_ADS = 'isLoadingAds'
export const GET_ADS_LOAD_RESULT = 'getAdsLoadResult'
export const GET_ADS_LOAD_ERROR = 'getAdsLoadError'
export const GET_SELECTED_AD = 'getSelectedAnimal'

export const IS_LOADING_ADS_META_DATA = 'isLoadingAdsMetaData'
export const GET_ADS_META_DATA_LOAD_RESULT = 'getAdsMetaDataLoadResult'
export const GET_ADS_META_DATA_LOAD_ERROR = 'getAdsMetaDataLoadError'

export const IS_LOADING_SELECTED_AD_PERIODS = 'isLoadingSelectedAdPeriods'
export const GET_SELECTED_AD_PERIODS_LOAD_RESULT = 'getSelectedAdPeriodsLoadResult'
export const GET_SELECTED_AD_PERIODS_LOAD_LOAD_ERROR = 'getSelectedAdPeriodsLoadError'

// Action names
export const LOAD_ADS_META_DATA = 'loadAdsMetaData'
export const LOAD_ADS = 'loadAds'
export const SELECT_AD_WITH_ID = 'selectAdWithId'
export const SAVE_AD = 'saveAd'
export const UPLOAD_IMAGE = 'uploadImage'
export const ADD_LANGUAGE_CODE = 'addLanguageCode'
export const SAVE_AD_PERIOD = 'saveAdPeriod'
export const LOAD_SELECTED_AD_PERIODS = 'loadSelectedAdPeriods'
export const DELETE_AD_PERIOD = 'deleteAdPeriod'

// Users store implementation
export const AdsStore = {
  namespaced: true,

  state: () => ({
    // state must be a function to allow reuse of this store
    adsMetaDataLoadState: new DataLoadState(),
    adsLoadState: new DataLoadState(),
    selectedAdId: null,
    selectedAdPeriodsState: new DataLoadState()
  }),

  mutations: {
    [SET_ADS_LOAD_STARTED](state, { loadStartTimestamp }) {
      const adsLoadState = new DataLoadState(loadStartTimestamp)
      state.adsLoadState = adsLoadState.stateForLoadStarted()
    },

    [SET_ADS_LOAD_FINISHED_SUCCESSFULLY](state, { loadStartTimestamp, ads }) {
      if (loadStartTimestamp !== state.adsLoadState.id) {
        // Load is stale, ignore
        return
      }
      state.adsLoadState = state.adsLoadState.stateForLoadFinished(ads)
    },

    [SET_ADS_LOAD_FAILED](state, { loadStartTimestamp, error }) {
      if (loadStartTimestamp !== state.adsLoadState.id) {
        // Load is stale, ignore
        return
      }
      state.adsLoadState = state.adsLoadState.stateForLoadFailed(error)
    },

    [SET_SELECTED_AD_ID](state, { adId }) {
      state.selectedAdId = adId
    },

    [SET_ADS_META_DATA_LOAD_STARTED](state, { loadStartTimestamp }) {
      const adsMetaDataLoadState = new DataLoadState(loadStartTimestamp)
      state.adsMetaDataLoadState = adsMetaDataLoadState.stateForLoadStarted()
    },

    [SET_ADS_META_DATA_LOAD_FINISHED_SUCCESSFULLY](state, { loadStartTimestamp, metaData }) {
      if (loadStartTimestamp !== state.adsMetaDataLoadState.id) {
        // Load is stale, ignore
        return
      }
      state.adsMetaDataLoadState = state.adsMetaDataLoadState.stateForLoadFinished(metaData)
    },

    [SET_ADS_META_DATA_LOAD_FAILED](state, { loadStartTimestamp, error }) {
      if (loadStartTimestamp !== state.adsMetaDataLoadState.id) {
        // Load is stale, ignore
        return
      }
      state.adsMetaDataLoadState = state.adsMetaDataLoadState.stateForLoadFailed(error)
    },

    [SET_SELECTED_AD_PERIODS_LOAD_STARTED](state, { adId }) {
      const selectedAdPeriodsState = new DataLoadState(adId)
      state.selectedAdPeriodsState = selectedAdPeriodsState.stateForLoadStarted()
    },

    [SET_SELECTED_AD_PERIODS_LOAD_FINISHED_SUCCESSFULLY](state, { adId, periods }) {
      if (adId !== state.selectedAdPeriodsState.id) {
        // Load is stale, ignore
        return
      }
      state.selectedAdPeriodsState = state.selectedAdPeriodsState.stateForLoadFinished(periods)
    },

    [SET_SELECTED_AD_PERIODS_LOAD_FAILED](state, { adId, error }) {
      if (adId !== state.selectedAdPeriodsState.id) {
        // Load is stale, ignore
        return
      }
      state.selectedAdPeriodsState = state.selectedAdPeriodsState.stateForLoadFailed(error)
    },

    [RESET_SELECTED_AD_PERIODS](state) {
      state.selectedAdPeriodsState = new DataLoadState()
    }
  },

  getters: {
    [IS_LOADING_ADS]: state => {
      return state.adsLoadState.status.loading
    },

    [GET_ADS_LOAD_RESULT]: state => {
      return state.adsLoadState.data
    },

    [GET_ADS_LOAD_ERROR]: state => {
      return state.adsLoadState.status.error
    },

    [GET_SELECTED_AD]: state => {
      if (!state.adsLoadState.data) {
        return null
      }
      if (!state.selectedAdId) {
        return null
      }
      return state.adsLoadState.data.find(ad => ad.id === state.selectedAdId)
    },

    [IS_LOADING_ADS_META_DATA]: state => {
      return state.adsMetaDataLoadState.status.loading
    },

    [GET_ADS_META_DATA_LOAD_RESULT]: state => {
      return state.adsMetaDataLoadState.data
    },

    [GET_ADS_META_DATA_LOAD_ERROR]: state => {
      return state.adsMetaDataLoadState.status.error
    },

    [IS_LOADING_SELECTED_AD_PERIODS]: state => {
      return state.selectedAdPeriodsState.status.loading
    },

    [GET_SELECTED_AD_PERIODS_LOAD_RESULT]: state => {
      return state.selectedAdPeriodsState.data
    },

    [GET_SELECTED_AD_PERIODS_LOAD_LOAD_ERROR]: state => {
      return state.selectedAdPeriodsState.status.error
    }
  },

  actions: {
    [LOAD_ADS]: async ({ commit }) => {
      // Start load
      const loadStartTimestamp = Date.now()
      commit(SET_ADS_LOAD_STARTED, { loadStartTimestamp })
      try {
        const response = await api.get('admin/ads')

        commit(SET_ADS_LOAD_FINISHED_SUCCESSFULLY, { loadStartTimestamp, ads: response.data })
      } catch (error) {
        commit(SET_ADS_LOAD_FAILED, { loadStartTimestamp, error })
      }
    },

    [LOAD_ADS_META_DATA]: async ({ commit }) => {
      // Start load
      const loadStartTimestamp = Date.now()
      commit(SET_ADS_META_DATA_LOAD_STARTED, { loadStartTimestamp })
      try {
        const response = await api.get('admin/ads/metaData')

        commit(SET_ADS_META_DATA_LOAD_FINISHED_SUCCESSFULLY, {
          loadStartTimestamp,
          metaData: response.data
        })
      } catch (error) {
        commit(SET_ADS_META_DATA_LOAD_FAILED, { loadStartTimestamp, error })
      }
    },

    [SELECT_AD_WITH_ID]: ({ state, commit }, { adId }) => {
      if (adId === state.selectedAdId) {
        // Selection hasn't changed
        return
      }

      commit(SET_SELECTED_AD_ID, { adId })
      commit(RESET_SELECTED_AD_PERIODS)
    },

    [UPLOAD_IMAGE]: async (_, { adId, imageFile }) => {
      const formData = new FormData() // eslint-disable-line
      formData.append('fileContents', imageFile)

      return api.post(`admin/ads/${adId}/image`, formData)
    },

    [SAVE_AD]: (_, { ad }) => {
      if (ad.id) {
        return api.put(`admin/ads/${ad.id}`, ad)
      } else {
        return api.post('admin/ads', ad)
      }
    },

    [ADD_LANGUAGE_CODE]: (_, { languageCode }) => {
      return api.post('admin/ads/language', { languageCode })
    },

    [SAVE_AD_PERIOD]: ({ commit, dispatch }, { adPeriod }) => {
      var savePromise

      if (adPeriod.id) {
        savePromise = api.put(`admin/ads/period/${adPeriod.id}`, adPeriod)
      } else {
        savePromise = api.post('admin/ads/period', adPeriod)
      }

      return savePromise.then(() => {
        // Always reload periods to make sure the list is correct.
        commit(RESET_SELECTED_AD_PERIODS)
        dispatch(LOAD_SELECTED_AD_PERIODS)
        dispatch(LOAD_ADS_META_DATA)
      })
    },

    [LOAD_SELECTED_AD_PERIODS]: async ({ commit, state }) => {
      const adId = state.selectedAdId
      if (!adId) {
        // No ad selected
        return
      }

      if (adId === state.selectedAdPeriodsState.id) {
        // Periods load has already started
        return
      }

      commit(SET_SELECTED_AD_PERIODS_LOAD_STARTED, { adId })

      try {
        const response = await api.get(`admin/ads/${adId}/periods`)

        const periods = response.data
        commit(SET_SELECTED_AD_PERIODS_LOAD_FINISHED_SUCCESSFULLY, { adId, periods })
      } catch (error) {
        commit(SET_SELECTED_AD_PERIODS_LOAD_FAILED, { adId, error })
      }
    },

    [DELETE_AD_PERIOD]: ({ commit, dispatch }, { adPeriodId }) => {
      return api.delete(`admin/ads/period/${adPeriodId}`).then(() => {
        // Always reload periods to make sure the list is correct.
        commit(RESET_SELECTED_AD_PERIODS)
        dispatch(LOAD_SELECTED_AD_PERIODS)
        dispatch(LOAD_ADS_META_DATA)
      })
    }
  }
}
